// Liquids on the map screens (docs/28 L6): the overworld's river and bog, and the local map's
// pools, move a little like the encounter's — sparkles drifting downstream, foam lapping at the
// banks, bubbles rising through the bog mud, rings where rain falls on the water (only under a
// passing front), snowflakes melting on it, and glints on the ice of a frozen river. Drawn on a
// transparent canvas laid over the `MapCanvas` terrain, at the atlas's 24 px, ~12 times a second.
// Pure apart from the canvas: `mapLiquids` decides the squares, `drawMapLiquids` paints a moment.
import { liquidCells, type Liquid } from '../../../game/liquid/liquid';
import { kitFor } from '../../../game/tiling';
import { LIQUID } from '../../../game/tiling/oryx';
import { liquidFill } from '../../../game/tiling/patterns';
import { terrainFromRows } from '../../components';
import type { MapProp } from './overworld';
import type { CustomChart } from './customChart';

const C = 24;
const MUD_FILLS = new Set(liquidFill(LIQUID.mud!));

export interface MapLiquidCell { x: number; y: number; liquid: Liquid; /** Bank on each side: n, e, s, w. */ bank: [boolean, boolean, boolean, boolean]; h: number }

export interface MapWeather {
  precip: 'rain' | 'snow' | 'sleet' | 'sand' | undefined;
  /** 0..1, how hard it falls. */
  amount: number;
  /** Ice over the water (0..1, docs/24 K6). */
  ice: number;
  /** Which columns of the chart are under falling weather now (fractions 0..1 of its width); undefined = all of it. */
  under?: () => Array<[number, number]> | undefined;
}

const hash = (x: number, y: number, s: number): number => {
  let h = (x * 73856093) ^ (y * 19349663) ^ (s * 83492791);
  h = Math.imul(h ^ (h >>> 13), 0x5bd1e995); h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
};

/** The liquid squares of a map: the rows read through the kit (`~` water, `,` bog in a mud kit) plus mud fills the screen painted. */
export function mapLiquids(rows: readonly string[], kit: string, fills: readonly MapProp[]): MapLiquidCell[] {
  const board = terrainFromRows(rows);
  const cells = liquidCells(board, kitFor(kit));
  for (const f of fills) if (MUD_FILLS.has(f.frame) && f.x >= 0 && f.y >= 0 && f.x < board.w && f.y < board.h && !cells[f.y * board.w + f.x]) cells[f.y * board.w + f.x] = 'mud';
  const body = (l: Liquid | undefined) => (l === 'deep' ? 'water' : l);
  const at = (x: number, y: number) => (x < 0 || y < 0 || x >= board.w || y >= board.h ? undefined : cells[y * board.w + x]);
  const out: MapLiquidCell[] = [];
  for (let y = 0; y < board.h; y++) for (let x = 0; x < board.w; x++) {
    const l = cells[y * board.w + x];
    if (!l) continue;
    const b = body(l);
    out.push({ x, y, liquid: l, bank: [body(at(x, y - 1)) !== b, body(at(x + 1, y)) !== b, body(at(x, y + 1)) !== b, body(at(x - 1, y)) !== b], h: hash(x, y, 1) });
  }
  return out;
}

/**
 * The liquid squares of a chart in the custom tileset (docs/70 CO10): its water and deep water, and the marsh's mud
 * where the country's marsh is wet (not the desert's salt pan). A bank is any side that isn't water.
 */
export function customLiquids(chart: CustomChart, dryMarsh = false): MapLiquidCell[] {
  const { w, h, cells } = chart;
  const kind = (x: number, y: number): Liquid | undefined => {
    if (x < 0 || y < 0 || x >= w || y >= h) return undefined;
    const t = cells[y * w + x]!.ground.tile;
    return t === 'water' ? 'water' : t === 'deep_water' ? 'deep' : t === 'marsh' && !dryMarsh ? 'mud' : undefined;
  };
  const body = (l: Liquid | undefined) => (l === 'deep' ? 'water' : l);
  const out: MapLiquidCell[] = [];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const l = kind(x, y);
    if (!l) continue;
    const b = body(l);
    // off the chart's edge the water runs on, as the chart's banks do
    const side = (nx: number, ny: number) => nx >= 0 && ny >= 0 && nx < w && ny < h && body(kind(nx, ny)) !== b;
    out.push({ x, y, liquid: l, bank: [side(x, y - 1), side(x + 1, y), side(x, y + 1), side(x - 1, y)], h: hash(x, y, 1) });
  }
  return out;
}

/** How a country's liquids look (CO10): the colour of the glints and rings, and whether to lap foam along the banks. */
export interface LiquidLook { glint?: string; ring?: string; foam?: boolean }

export interface MapRing { x: number; y: number; born: number; life: number; size: number; mud: boolean }

/** Live rings and the clock that spawns them (kept by the component between frames). */
export interface MapLiquidState { rings: MapRing[]; last: number }

/** Spawn this frame's rain rings and snow dimples on the squares under the weather. */
export function spawnRings(state: MapLiquidState, cells: readonly MapLiquidCell[], w: MapWeather, t: number, mapW: number, rand: () => number = Math.random): void {
  const dt = state.last ? Math.min(0.25, t - state.last) : 0;
  state.last = t;
  state.rings = state.rings.filter((r) => t - r.born < r.life);
  if (!w.precip || w.precip === 'sand' || w.ice >= 0.5 || dt <= 0) return;
  const spans = w.under?.();
  const under = (x: number) => !spans || spans.some(([a, b]) => (x + 0.5) / mapW >= a && (x + 0.5) / mapW <= b);
  const snow = w.precip === 'snow';
  const rate = (snow ? 0.35 : w.precip === 'sleet' ? 0.6 : 1) * w.amount * 1.4;   // per square per second
  for (const c of cells) {
    if (c.liquid === 'lava' || !under(c.x)) continue;
    if (rand() > rate * dt) continue;
    state.rings.push({ x: c.x * C + 6 + rand() * 12, y: c.y * C + 6 + rand() * 12, born: t, life: snow ? 0.9 : 0.6, size: snow ? 1.2 : 3.5 + rand(), mud: c.liquid === 'mud' });
  }
}

/** Paint one moment of the map's liquids (t in seconds). */
export function drawMapLiquids(ctx: CanvasRenderingContext2D, cells: readonly MapLiquidCell[], state: MapLiquidState, w: MapWeather | undefined, t: number, look: LiquidLook = {}): void {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  const glint = look.glint ?? '255,255,255';
  const px = (x: number, y: number, a: number, rgb = glint) => { if (a <= 0.01) return; ctx.fillStyle = `rgba(${rgb},${a.toFixed(3)})`; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
  const iced = (w?.ice ?? 0) >= 0.5;
  for (const c of cells) {
    const ox = c.x * C, oy = c.y * C;
    const [n, e, s, wst] = c.bank;
    // the inner box clear of the shore art
    const x0 = ox + (wst ? 4 : 0), x1 = ox + C - (e ? 4 : 0), y0 = oy + (n ? 4 : 0), y1 = oy + C - (s ? 4 : 0);
    if (c.liquid === 'water' || c.liquid === 'deep') {
      if (iced) {
        // ice: a glint or two coming and going
        for (let i = 0; i < 2; i++) {
          const gx = x0 + hash(c.x, c.y, 10 + i) * (x1 - x0), gy = y0 + hash(c.x, c.y, 20 + i) * (y1 - y0);
          const a = Math.pow(Math.max(0, Math.sin(t * 1.7 + c.h * 40 + i * 2)), 12) * 0.95;
          px(gx, gy, a); px(gx - 1, gy, a * 0.5); px(gx + 1, gy, a * 0.5); px(gx, gy - 1, a * 0.5); px(gx, gy + 1, a * 0.5);
        }
        continue;
      }
      // sparkles carried downstream (south) and with the current's wobble
      for (let i = 0; i < 3; i++) {
        const span = y1 - y0;
        const gy = y0 + ((hash(c.x, c.y, 30 + i) * span + t * 5) % span);
        const gx = x0 + hash(c.x, c.y, 40 + i) * (x1 - x0 - 2) + Math.sin(t * 0.9 + i + c.h * 9) * 1.2;
        const a = Math.pow(Math.max(0, Math.sin(t * 2.1 + c.h * 31 + i * 2.3)), 6) * 0.85;
        px(gx, gy, a); px(gx + 1, gy, a * 0.6);
      }
      // foam lapping in and out along each bank
      const lap = 4 + Math.round(1.4 * (0.5 + 0.5 * Math.sin(t * 1.3 + c.h * 6.3)));
      const foam = (fx: number, fy: number) => { if (hash(Math.round(fx), Math.round(fy), Math.floor(t * 2)) > 0.3) px(fx, fy, 0.32, '235,244,255'); };
      for (let i = 4; i < C - 4 && look.foam !== false; i++) {
        if (n) foam(ox + i, oy + lap);
        if (s) foam(ox + i, oy + C - 1 - lap);
        if (wst) foam(ox + lap, oy + i);
        if (e) foam(ox + C - 1 - lap, oy + i);
      }
    } else if (c.liquid === 'mud') {
      if (iced) continue;
      // a fat bubble swells, catches the light and pops, every few seconds per square
      const period = 4 + c.h * 4;
      const ph = (t / period + c.h * 7) % 1;
      const bx = ox + 8 + hash(c.x, c.y, Math.floor(t / period + c.h * 7)) * 8, by = oy + 8 + hash(c.y, c.x, Math.floor(t / period + c.h * 7)) * 8;
      if (ph < 0.14) {
        const r = 0.5 + (ph / 0.14) * 1.8;
        ctx.fillStyle = 'rgba(40,28,14,0.55)';
        ctx.fillRect(Math.round(bx - r), Math.round(by - r * 0.7), Math.max(1, Math.round(r * 2)), Math.max(1, Math.round(r * 1.4)));
        if (r > 1.2) px(bx - r * 0.5, by - r * 0.5, 0.6);
      } else if (ph < 0.22) {
        ring(ctx, bx, by, 2 + ((ph - 0.14) / 0.08) * 2.5, 0.5 * (1 - (ph - 0.14) / 0.08), '170,140,100');
      }
    }
  }
  for (const r of state.rings) {
    const k = (t - r.born) / r.life;
    if (r.size < 2) { if (k < 0.4) px(r.x, r.y, 0.75 * (1 - k)); else ring(ctx, r.x, r.y, 1.2, 0.5 * (1 - k), '240,246,255'); continue; }
    ring(ctx, r.x, r.y, 1 + k * r.size, (r.mud ? 0.35 : 0.6) * (1 - k), r.mud ? '170,150,120' : look.ring ?? '225,236,255');
  }
}

/** A flat pixel ring (the water seen at a slant). */
function ring(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, a: number, rgb: string): void {
  if (a <= 0.01) return;
  ctx.fillStyle = `rgba(${rgb},${a.toFixed(3)})`;
  const n = Math.max(8, Math.round(r * 6));
  const seen = new Set<number>();
  for (let i = 0; i < n; i++) {
    const ang = (i / n) * Math.PI * 2;
    const rx = Math.round(x + Math.cos(ang) * r), ry = Math.round(y + Math.sin(ang) * r * 0.6);
    const key = rx * 4096 + ry;
    if (seen.has(key)) continue;
    seen.add(key);
    ctx.fillRect(rx, ry, 1, 1);
  }
}
